import React, { useState } from 'react';
import {
    ArrowLeft,
    Calendar,
    Clock,
    Calculator,
    Plus,
    Trash2,
    AlertCircle,
    CheckCircle2,
    TrendingUp,
    Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { format, addDays } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import toast from 'react-hot-toast';

// Cores por cargo
const ROLE_COLORS = {
    GERENCIA: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    RECEPCAO_COMPRAS_AGENDA: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    RECEPCAO_PROJETOS: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
    PRODUCAO: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    MONTAGEM_CLIENTE: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' }
};

const ROLE_LABELS = {
    GERENCIA: 'Gerência',
    RECEPCAO_COMPRAS_AGENDA: 'Compras/Agenda',
    RECEPCAO_PROJETOS: 'Recepção Projetos',
    PRODUCAO: 'Produção',
    MONTAGEM_CLIENTE: 'Montagem Cliente'
};

const DEFAULT_HOURS_PER_DAY = {
    GERENCIA: 4,
    RECEPCAO_COMPRAS_AGENDA: 8,
    RECEPCAO_PROJETOS: 8,
    PRODUCAO: 8,
    MONTAGEM_CLIENTE: 8
};

const ProjectSimulator = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [hoursByRole, setHoursByRole] = useState([
        { role: 'PRODUCAO', hours: 40, hours_per_day: 8 }
    ]);
    const [result, setResult] = useState(null);

    const availableRoles = Object.keys(ROLE_LABELS);

    const addRole = () => {
        const nextRole = availableRoles.find(r => !hoursByRole.find(h => h.role === r));
        if (nextRole) {
            setHoursByRole([
                ...hoursByRole,
                { role: nextRole, hours: 0, hours_per_day: DEFAULT_HOURS_PER_DAY[nextRole] }
            ]);
        } else {
            toast.error('Todos os cargos já foram adicionados');
        }
    };

    const removeRole = (index) => {
        setHoursByRole(hoursByRole.filter((_, i) => i !== index));
    };

    const updateRole = (index, field, value) => {
        setHoursByRole(hoursByRole.map((item, i) => {
            if (i === index) {
                const updated = { ...item, [field]: value };
                // Se mudou role, atualizar horas_per_day padrão
                if (field === 'role') {
                    updated.hours_per_day = DEFAULT_HOURS_PER_DAY[value];
                }
                return updated;
            }
            return item;
        }));
    };

    const handleSimulate = async () => {
        // Validação
        const validHours = hoursByRole.filter(h => parseFloat(h.hours) > 0);
        if (validHours.length === 0) {
            toast.error('Adicione pelo menos um cargo com horas maiores que zero');
            return;
        }

        try {
            setLoading(true);
            const response = await api.post('/capacity/simulate-delivery', {
                hours_by_role: validHours.map(h => ({
                    role: h.role,
                    hours: parseFloat(h.hours),
                    hours_per_day: parseFloat(h.hours_per_day)
                })),
                start_date: startDate
            });

            setResult(response.data.data);
            toast.success('Simulação concluída!');
        } catch (error) {
            console.error('Erro na simulação:', error);
            toast.error('Erro ao simular prazo de entrega');
        } finally {
            setLoading(false);
        }
    };

    const totalHours = hoursByRole.reduce((sum, h) => sum + parseFloat(h.hours || 0), 0);

    return (
        <div className="container mx-auto px-4 py-6 max-w-4xl">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/capacidade')}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Calculator className="w-7 h-7 text-[#3a7630]" />
                        Simulador de Prazo de Entrega
                    </h1>
                    <p className="text-gray-600">
                        Calcule a data de entrega estimada baseada na carga de trabalho por cargo
                    </p>
                </div>
            </div>

            {/* Formulário */}
            <div className="bg-white rounded-lg shadow mb-6">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="font-semibold text-gray-900">Configuração do Projeto</h2>
                </div>

                <div className="p-4 space-y-4">
                    {/* Data de Início */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Data de Início Prevista
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            A simulação considera apenas dias úteis (segunda a sexta), excluindo feriados
                        </p>
                    </div>

                    {/* Lista de Cargos/Horas */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                <Clock className="w-4 h-4 inline mr-1" />
                                Horas por Cargo
                            </label>
                            <button
                                onClick={addRole}
                                className="flex items-center gap-1 text-sm text-[#3a7630] hover:text-[#2d5a25]"
                            >
                                <Plus className="w-4 h-4" />
                                Adicionar Cargo
                            </button>
                        </div>

                        <div className="space-y-3">
                            {hoursByRole.map((item, index) => {
                                const colors = ROLE_COLORS[item.role] || ROLE_COLORS.PRODUCAO;

                                return (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                                {/* Cargo */}
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">
                                                        Cargo
                                                    </label>
                                                    <select
                                                        value={item.role}
                                                        onChange={(e) => updateRole(index, 'role', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                                                    >
                                                        {availableRoles.map(role => (
                                                            <option key={role} value={role}>
                                                                {ROLE_LABELS[role]}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Horas Totais */}
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">
                                                        Horas Totais
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.5"
                                                        value={item.hours}
                                                        onChange={(e) => updateRole(index, 'hours', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                                                    />
                                                </div>

                                                {/* Horas por Dia */}
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">
                                                        Horas por Dia
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="24"
                                                        step="0.5"
                                                        value={item.hours_per_day}
                                                        onChange={(e) => updateRole(index, 'hours_per_day', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3a7630]"
                                                    />
                                                </div>
                                            </div>

                                            {/* Remover */}
                                            {hoursByRole.length > 1 && (
                                                <button
                                                    onClick={() => removeRole(index)}
                                                    className="p-2 text-red-500 hover:text-red-700"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Preview */}
                                        {parseFloat(item.hours) > 0 && parseFloat(item.hours_per_day) > 0 && (
                                            <div className="mt-2 text-xs text-gray-600">
                                                Dias úteis necessários: <strong>{Math.ceil(parseFloat(item.hours) / parseFloat(item.hours_per_day))}</strong> dias
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Total */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <span className="font-medium text-gray-700">Total de Horas do Projeto</span>
                        <span className="text-xl font-bold text-[#3a7630]">{totalHours.toFixed(1)}h</span>
                    </div>

                    {/* Botão Simular */}
                    <button
                        onClick={handleSimulate}
                        disabled={loading || totalHours === 0}
                        className="w-full flex items-center justify-center gap-2 bg-[#3a7630] text-white py-3 rounded-lg hover:bg-[#2d5a25] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Calculando...
                            </>
                        ) : (
                            <>
                                <Calculator className="w-5 h-5" />
                                Simular Prazo de Entrega
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Resultado */}
            {result && (
                <div className="bg-white rounded-lg shadow">
                    <div className="p-4 border-b border-gray-200 bg-green-50">
                        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            Resultado da Simulação
                        </h2>
                    </div>

                    <div className="p-4 space-y-4">
                        {/* Data de Entrega */}
                        <div className="text-center p-6 bg-gradient-to-r from-[#3a7630] to-[#2d5a25] rounded-lg text-white">
                            <p className="text-sm opacity-80 mb-1">Data Estimada de Entrega</p>
                            <p className="text-4xl font-bold">{result.overall_delivery_formatted}</p>
                            <p className="text-sm opacity-80 mt-2">
                                Considerando a carga atual da equipe
                            </p>
                        </div>

                        {/* Detalhamento por Cargo */}
                        <div>
                            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-gray-600" />
                                Detalhamento por Cargo
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {result.simulation_details.map((detail, idx) => {
                                    const colors = ROLE_COLORS[detail.role] || ROLE_COLORS.PRODUCAO;
                                    const isCriticalPath = result.critical_path.role === detail.role;

                                    return (
                                        <div
                                            key={idx}
                                            className={`p-4 rounded-lg border ${colors.bg} ${colors.border} ${isCriticalPath ? 'ring-2 ring-yellow-400' : ''}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <span className={`text-sm font-semibold ${colors.text}`}>
                                                        {ROLE_LABELS[detail.role] || detail.role}
                                                    </span>
                                                    {isCriticalPath && (
                                                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                                            Caminho Crítico
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mt-3 space-y-1 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Horas totais:</span>
                                                    <span className="font-medium">{detail.total_hours}h</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Por dia:</span>
                                                    <span className="font-medium">{detail.hours_per_day}h</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Dias úteis:</span>
                                                    <span className="font-medium">{detail.days_needed}</span>
                                                </div>
                                                <div className="flex justify-between pt-2 border-t border-gray-200">
                                                    <span className="text-gray-700 font-medium">Entrega:</span>
                                                    <span className="font-bold text-gray-900">
                                                        {detail.delivery_date_formatted}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Observações */}
                        <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-lg">
                            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <strong>Observações:</strong>
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                    <li>A simulação considera apenas dias úteis (segunda a sexta)</li>
                                    <li>Feriados são automaticamente excluídos</li>
                                    <li>O "Caminho Crítico" é o cargo que determina a data final de entrega</li>
                                    <li>Esta é uma estimativa baseada na disponibilidade atual da equipe</li>
                                </ul>
                            </div>
                        </div>

                        {/* Botão para Cliente */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    const text = `
🎯 **Simulação de Prazo de Entrega**

**Projeto:** ${totalHours.toFixed(1)} horas totais

📅 **Data de Início:** ${format(new Date(startDate), "dd/MM/yyyy")}
✅ **Data Estimada de Entrega:** ${result.overall_delivery_formatted}

**Detalhamento por setor:**
${result.simulation_details.map(d => `• ${ROLE_LABELS[d.role] || d.role}: ${d.total_hours}h → ${d.delivery_date_formatted}`).join('\n')}

⚠️ *Esta é uma estimativa baseada na disponibilidade atual da equipe.*
                                    `;
                                    navigator.clipboard.writeText(text);
                                    toast.success('Resumo copiado para compartilhar com o cliente!');
                                }}
                                className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                <AlertCircle className="w-4 h-4" />
                                Copiar Resumo para Cliente
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectSimulator;
